import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { buildOrgUrl, getOrgSlugFromHostname } from '@/core/domain';
import { setToken } from '@/core/token';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export const VerifySignupForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const me = useMe();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('auth');

  const secret = searchParams.get('secret') || '';
  const email = searchParams.get('email') || '';

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.code) {
        errors.code = t('verifySignup.enterVerificationCode');
      } else if (values.code.length !== 6) {
        errors.code = t('verifySignup.codeMust6Digits');
      }
      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      const toastId = toast.loading(t('verifySignup.verifyingCode'), {
        description: t('verifySignup.verifyingDescription'),
      });

      try {
        const captchaToken = await executeRecaptcha?.('verify_signup');

        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/verify-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            secret,
            code: values.code,
            captchaToken,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          formik.setFieldError('code', data.error || t('verifySignup.invalidCode'));
          throw new Error(data.error || t('verifySignup.invalidCode'));
        }

        if (data.success && data.token) {
          setToken(data.token);
          me.loadMe();
          toast.success(t('verifySignup.emailVerified'), {
            description: t('verifySignup.emailVerifiedDescription'),
            id: toastId,
          });
          const slugs: string[] | undefined = data.organizationSlugs;
          const currentSlug = getOrgSlugFromHostname();
          if (currentSlug && slugs?.includes(currentSlug)) {
            navigate('/');
          } else if (slugs && slugs.length > 0) {
            window.location.href = buildOrgUrl(slugs[0]);
          } else {
            navigate('/auth/onboarding/org');
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('verifySignup.verificationFailed');
        setError(errorMessage);
        toast.error(t('verifySignup.verificationFailed'), {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleResendCode = async () => {
    setIsResending(true);
    const toastId = toast.loading(t('verifySignup.resendingCode'), {
      description: t('verifySignup.resendingDescription'),
    });

    try {
      const captchaToken = await executeRecaptcha?.('resend_signup');

      const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/resend-signup-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          captchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('verifySignup.failedResendCode'));
      }

      if (data.success) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('secret', data.secret);
        setSearchParams(newParams);
        formik.resetForm();
        setError(null);
        toast.success(t('verifySignup.newCodeSent'), {
          description: t('verifySignup.newCodeSentDescription'),
          id: toastId,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('verifySignup.failedResendCode');
      toast.error(t('verifySignup.failedResendCode'), {
        description: errorMessage,
        id: toastId,
      });
    } finally {
      setIsResending(false);
    }
  };

  if (!secret || !email) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='p-6 md:p-8 bg-white dark:bg-stone-900'>
            <div className='flex flex-col items-center text-center gap-4'>
              <img src={logo} alt='Logo' className='size-16' />
              <h1 className='text-2xl font-bold'>{t('verifySignup.invalidLink')}</h1>
              <p className='text-muted-foreground'>{t('verifySignup.invalidLinkDescription')}</p>
              <Link to='/auth/signup'>
                <Button>{t('verifySignup.backToSignUp')}</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <img src={logo} alt='Logo' className='size-16 mb-6' />
                <h1 className='text-2xl font-bold'>{t('verifySignup.title')}</h1>
                <p className='text-muted-foreground text-balance'>{t('verifySignup.codeSentTo', { email })}</p>
                {error && (
                  <div className='mt-4 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-md'>
                    <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
                  </div>
                )}
              </div>
              <div className='grid gap-2'>
                <Label>{t('verifySignup.verificationCode')}</Label>
                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                  value={formik.values.code}
                  onChange={(value) => {
                    formik.setFieldValue('code', value);
                    if (formik.errors.code) {
                      formik.setFieldError('code', '');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') formik.handleSubmit();
                  }}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {formik.touched.code && formik.errors.code && (
                  <p className='text-sm text-destructive'>{formik.errors.code}</p>
                )}
              </div>
              <Button type='submit' className='w-full' disabled={isLoading}>
                {isLoading ? t('verifySignup.verifying') : t('verifySignup.verifyEmail')}
              </Button>
              <div className='flex flex-col gap-2 text-center'>
                <Button type='button' variant='link' onClick={handleResendCode} disabled={isResending || isLoading}>
                  {isResending ? t('verifySignup.resending') : t('verifySignup.resend')}
                </Button>
                <Link
                  to='/auth/signup'
                  className='text-sm text-muted-foreground underline underline-offset-4 hover:text-primary'>
                  {t('verifySignup.backToSignUp')}
                </Link>
              </div>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block p-4'>
            <div className='absolute inset-0 p-4'>
              <img
                src='/login.png'
                alt='Image'
                className='w-full h-full object-contain '
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
